import { useState, useCallback } from "react";
import { toast } from "react-toastify";

export interface FlippMerchant {
  id: number;
  name: string;
  postalCode: string;
}

interface FlippApiResponse {
  flyers: Array<{
    merchant: string;
    merchant_id: number;
  }>;
}

/**
 * Custom hook to fetch merchants from Flipp API based on postal code
 */
export const useFlippMerchants = () => {
  const [flippMerchants, setFlippMerchants] = useState<FlippMerchant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFlippMerchants = useCallback(async (postalCode: string) => {
    if (!postalCode) {
      setFlippMerchants([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const cleanedPostalCode = postalCode.replace(/\s/g, "");

      const response = await fetch(
        `https://dam.flippenterprise.net/api/flipp/data?locale=en&postal_code=${cleanedPostalCode}&sid=3039433830574545`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch merchants: ${response.statusText}`);
      }

      const data: FlippApiResponse = await response.json();

      const merchantMap = new Map<number, string>();

      if (data.flyers && Array.isArray(data.flyers)) {
        data.flyers.forEach((flyer) => {
          if (flyer.merchant_id && flyer.merchant) {
            merchantMap.set(flyer.merchant_id, flyer.merchant);
          }
        });
      }

      const merchants: FlippMerchant[] = Array.from(merchantMap.entries()).map(
        ([id, name]) => ({ id, name, postalCode: cleanedPostalCode })
      );

      merchants.sort((a, b) => a.name.localeCompare(b.name));

      setFlippMerchants(merchants);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch Flipp merchants";
      setError(errorMessage);
      setFlippMerchants([]);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    flippMerchants,
    isLoading,
    error,
    fetchFlippMerchants,
  };
};
